import { Alert, Box, Button, Center, Group, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { LuArrowRight, LuFlaskConical, LuLogOut, LuMail } from 'react-icons/lu'
import { TbHourglass } from 'react-icons/tb'
import { ThemeToggle } from '@/frontend/components/ThemeToggle'
import { useLogout, useSession } from '@/frontend/hooks/useAuth'

export const Route = createFileRoute('/pending')({
  component: PendingPage,
})

/**
 * Landing page for newly-signed-up Google OAuth users (role=PENDING).
 *
 * Tone is welcoming, not punitive: explains the B2B invite model (marina
 * staff are added by their admin), offers the demo as the immediate try-now
 * path, and gives a clear contact channel for prospects.
 */
function PendingPage() {
  const session = useSession()
  const logout = useLogout()
  const userName = session.data?.user?.name ?? 'Calon pengguna'

  return (
    <Center mih="100vh" px="md">
      <Box style={{ position: 'absolute', top: 16, right: 16 }}>
        <ThemeToggle />
      </Box>

      <Paper p="xl" radius="md" maw={520} w="100%">
        <Stack gap="lg">
          <Group gap="md" wrap="nowrap">
            <ThemeIcon color="karang" size={56} radius="xl" variant="light">
              <TbHourglass size={28} />
            </ThemeIcon>
            <div>
              <Title order={3} mb={2}>
                Halo, {userName}!
              </Title>
              <Text size="sm" c="dimmed">
                Akun Anda berhasil dibuat — tinggal satu langkah lagi.
              </Text>
            </div>
          </Group>

          <Text size="sm">
            JP MARINE adalah software untuk operator marina. Akses staff diberikan oleh{' '}
            <strong>admin marina tempat Anda bekerja</strong>. Hubungi admin Anda untuk diundang.
          </Text>

          <Alert color="laut" variant="light" icon={<LuFlaskConical size={18} />}>
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Hanya ingin lihat-lihat dulu?
              </Text>
              <Text size="sm">
                Coba <strong>Mode Demo Live</strong> — akses penuh ke marina contoh dengan data lengkap, tanpa perlu
                undangan. Data direset setiap hari.
              </Text>
              <Group gap="xs" mt={4}>
                <DemoButton />
                <Button
                  component={Link}
                  to="/contact"
                  variant="default"
                  size="sm"
                  rightSection={<LuArrowRight size={14} />}
                >
                  Hubungi Sales
                </Button>
              </Group>
            </Stack>
          </Alert>

          <Alert color="gray" variant="light" icon={<LuMail size={16} />}>
            <Text size="sm">
              Sudah diundang tapi masih melihat halaman ini? Kemungkinan email yang dipakai berbeda. Kontak admin marina
              Anda atau email <strong>support@open-marina.id</strong>.
            </Text>
          </Alert>

          <Button
            variant="subtle"
            color="gray"
            leftSection={<LuLogOut size={16} />}
            onClick={() => logout.mutate()}
            loading={logout.isPending}
            fullWidth
          >
            Keluar
          </Button>
        </Stack>
      </Paper>
    </Center>
  )
}

/** Mints a demo session and hard-navigates to /dashboard. Hidden when ENABLE_DEMO=false. */
function DemoButton() {
  const [pending, setPending] = useState(false)
  const config = useQuery({
    queryKey: ['demo', 'config'],
    queryFn: async () => {
      const res = await fetch('/api/demo/config')
      if (!res.ok) return { enabled: false }
      return (await res.json()) as { enabled: boolean }
    },
    retry: false,
    staleTime: 5 * 60_000,
  })

  if (!config.data?.enabled) return null

  async function start() {
    setPending(true)
    try {
      const res = await fetch('/api/demo/start', { method: 'POST', credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = (await res.json()) as { redirectTo?: string }
      window.location.assign(body.redirectTo ?? '/dashboard')
    } catch (e) {
      setPending(false)
      console.error('[Demo] start failed', e)
    }
  }

  return (
    <Button onClick={start} loading={pending} color="laut" size="sm" leftSection={<LuFlaskConical size={14} />}>
      Coba Mode Demo
    </Button>
  )
}
